import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, ChevronDown, ArrowLeft } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { formatDate as fmtDateUtil } from '../../utils/dateUtils';
import api from '../../services/api';
import {
  LoadingState, DonutChart, ConversionBadge, StatusPipeline,
  QuoteStatusPill, fmt
} from './ReportHelpers';

const fmtDate = (d) => fmtDateUtil(d);

function CustomerConversionChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-[10px] border p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Customer Quote Conversion</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="Quoted" fill="#94a3b8" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Accepted" fill="#38a169" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Declined" fill="#e53e3e" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomerRevenueChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-[10px] border p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Customer Revenue Breakdown</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
          <Tooltip formatter={(value) => `$${value.toLocaleString('en-AU', {minimumFractionDigits: 2})}`} />
          <Legend />
          <Bar dataKey="Quoted" fill="#f4931d" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Invoiced" fill="#178c98" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Paid" fill="#38a169" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ClientDrilldown({ client }) {
  return (
    <div className="bg-slate-50 border-t p-5 space-y-4" data-testid={`client-drilldown-${client.client_id}`}>
      <div className="flex items-center gap-3 mb-2">
        <h4 className="font-semibold text-slate-800">{client.client_name}</h4>
        <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">{client.client_type}</span>
        {client.referral !== '-' && <span className="text-xs text-slate-500">Referral: {client.referral}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white rounded border p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Avg Quote</div>
          <div className="text-sm font-bold text-slate-800">{fmt(client.avg_quote_value)}</div>
        </div>
        <div className="bg-white rounded border p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Jobs</div>
          <div className="text-sm font-bold text-slate-800">{client.job_count} <span className="text-xs font-normal text-slate-400">({client.completed_jobs} done)</span></div>
        </div>
        <div className="bg-white rounded border p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Accepted Value</div>
          <div className="text-sm font-bold text-green-700">{fmt(client.accepted_value)}</div>
        </div>
        <div className="bg-white rounded border p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Outstanding</div>
          <div className={`text-sm font-bold ${client.outstanding > 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmt(client.outstanding)}</div>
        </div>
        <div className="bg-white rounded border p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">First Quote</div>
          <div className="text-sm font-bold text-slate-800">{fmtDate(client.first_quote_date)}</div>
        </div>
        <div className="bg-white rounded border p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Last Quote</div>
          <div className="text-sm font-bold text-slate-800">{fmtDate(client.last_quote_date)}</div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <StatusPipeline breakdown={client.status_breakdown} title="Quote Pipeline" />
        <div className="bg-white rounded border p-4 max-h-64 overflow-y-auto">
          <h5 className="text-xs font-semibold text-slate-600 mb-3">All Quotes</h5>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 border-b">
                <th className="text-left pb-1.5 font-medium">Quote #</th>
                <th className="text-left pb-1.5 font-medium">Date</th>
                <th className="text-left pb-1.5 font-medium">Status</th>
                <th className="text-right pb-1.5 font-medium">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(client.quotes_detail || []).map((q, i) => (
                <tr key={q.quote_number || `q-${i}`} className="hover:bg-slate-50">
                  <td className="py-1.5 font-medium text-slate-700">{q.quote_number}</td>
                  <td className="py-1.5 text-slate-500">{fmtDate(q.date)}</td>
                  <td className="py-1.5"><QuoteStatusPill status={q.status} /></td>
                  <td className="py-1.5 text-right font-medium">{fmt(q.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ClientDetailView({ detail, loading, onExport }) {
  const [expandedProp, setExpandedProp] = useState(null);

  if (loading) return <LoadingState />;
  if (!detail) return null;

  const { overall, properties, property_chart } = detail;

  const handleExport = () => {
    const headers = ['Property', 'Quotes', 'Accepted', 'Declined', 'Conversion %', 'Quoted', 'Invoiced', 'Paid', 'Outstanding', 'Jobs'];
    const rows = properties.map(p => [
      p.address, p.quote_count, p.accepted_count, p.declined_count,
      `${p.conversion_rate}%`, p.total_quoted, p.total_invoiced, p.total_paid, p.outstanding, p.job_count
    ]);
    onExport(`${overall.client_name} - Property Report`, headers, rows, null);
  };

  return (
    <div className="space-y-5" data-testid="client-detail-view">
      <div className="bg-white rounded-[10px] border p-5">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-xl font-bold text-slate-800">{overall.client_name}</h2>
          <span className="text-xs bg-slate-200 px-2 py-1 rounded font-medium">{overall.client_type}</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">{overall.property_count} {overall.property_count === 1 ? 'property' : 'properties'}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Quotes</div>
            <div className="text-lg font-bold text-slate-800">{overall.quote_count}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Accepted</div>
            <div className="text-lg font-bold text-green-700">{overall.accepted_count}</div>
            <div className="text-[10px] text-red-500">{overall.declined_count} declined</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Conversion</div>
            <div className="text-lg font-bold text-[#f4931d]">{overall.conversion_rate}%</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Total Quoted</div>
            <div className="text-lg font-bold text-slate-800">{fmt(overall.total_quoted)}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Invoiced</div>
            <div className="text-lg font-bold text-slate-800">{fmt(overall.total_invoiced)}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Outstanding</div>
            <div className={`text-lg font-bold ${overall.outstanding > 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmt(overall.outstanding)}</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <StatusPipeline breakdown={overall.status_breakdown} title="Overall Quote Pipeline" />
        {property_chart && property_chart.length > 1 && (
          <div className="bg-white rounded-[10px] border p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Conversion by Property</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={property_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Quoted" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Accepted" fill="#38a169" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Declined" fill="#e53e3e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">Property Breakdown</h3>
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 print:hidden" data-testid="export-property-excel">
          <FileSpreadsheet size={14} /> Export Excel
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="text-center py-8 text-slate-400 bg-white rounded-[10px] border">No properties with activity in this period</div>
      ) : (
        <div className="space-y-3">
          {properties.map(prop => (
            <div key={prop.property_id} className="bg-white rounded-[10px] border overflow-hidden" data-testid={`property-card-${prop.property_id}`}>
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/60 transition-colors"
                onClick={() => setExpandedProp(expandedProp === prop.property_id ? null : prop.property_id)}
              >
                <div className="flex items-center gap-3">
                  <ChevronDown size={16} className={`text-slate-400 transition-transform ${expandedProp === prop.property_id ? 'rotate-180' : ''}`} />
                  <div>
                    <div className="font-medium text-slate-800">{prop.address}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {prop.tenant_name !== '-' && <span>Tenant: {prop.tenant_name}</span>}
                      {prop.owner_name !== '-' && <span className="ml-3">Owner: {prop.owner_name}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-5 text-sm">
                  <div className="text-center">
                    <div className="text-xs text-slate-400">Quotes</div>
                    <div className="font-semibold">{prop.quote_count}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-slate-400">Accepted</div>
                    <div className="font-semibold text-green-700">{prop.accepted_count}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-slate-400">Conversion</div>
                    <ConversionBadge rate={prop.conversion_rate} />
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-slate-400">Quoted</div>
                    <div className="font-semibold">{fmt(prop.total_quoted)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-slate-400">Invoiced</div>
                    <div className="font-semibold">{fmt(prop.total_invoiced)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-slate-400">Jobs</div>
                    <div className="font-semibold">{prop.job_count}</div>
                  </div>
                </div>
              </div>

              {expandedProp === prop.property_id && (
                <div className="border-t bg-slate-50 p-5 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-white rounded border p-3">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Accepted Value</div>
                      <div className="text-sm font-bold text-green-700">{fmt(prop.accepted_value)}</div>
                    </div>
                    <div className="bg-white rounded border p-3">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Total Paid</div>
                      <div className="text-sm font-bold text-green-700">{fmt(prop.total_paid)}</div>
                    </div>
                    <div className="bg-white rounded border p-3">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Outstanding</div>
                      <div className={`text-sm font-bold ${prop.outstanding > 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmt(prop.outstanding)}</div>
                    </div>
                    <div className="bg-white rounded border p-3">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Jobs</div>
                      <div className="text-sm font-bold text-slate-800">{prop.job_count} <span className="text-xs font-normal text-slate-400">({prop.completed_jobs} done)</span></div>
                    </div>
                    <div className="bg-white rounded border p-3">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">Invoices</div>
                      <div className="text-sm font-bold text-slate-800">{prop.invoice_count}</div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <StatusPipeline breakdown={prop.status_breakdown} title="Quote Pipeline" />
                    <div className="bg-white rounded border p-4 max-h-72 overflow-y-auto">
                      <h5 className="text-xs font-semibold text-slate-600 mb-3">Quotes for this Property</h5>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-400 border-b">
                            <th className="text-left pb-1.5 font-medium">Quote #</th>
                            <th className="text-left pb-1.5 font-medium">Date</th>
                            <th className="text-left pb-1.5 font-medium">Job Titles</th>
                            <th className="text-left pb-1.5 font-medium">Status</th>
                            <th className="text-right pb-1.5 font-medium">Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(prop.quotes_detail || []).map((q, i) => (
                            <tr key={q.quote_number || `pq-${i}`} className="hover:bg-slate-50">
                              <td className="py-1.5 font-medium text-slate-700">{q.quote_number}</td>
                              <td className="py-1.5 text-slate-500">{fmtDate(q.date)}</td>
                              <td className="py-1.5 text-slate-500 max-w-[200px] truncate">{q.job_titles || '-'}</td>
                              <td className="py-1.5"><QuoteStatusPill status={q.status} /></td>
                              <td className="py-1.5 text-right font-medium">{fmt(q.value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientsTab({ data, loading, onExport, startDate, endDate }) {
  const [expandedClient, setExpandedClient] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('all');

  const clientDetail = useQuery({
    queryKey: ['report-client-detail', selectedClientId, startDate, endDate],
    queryFn: () => api.get(`/api/reports/clients/${selectedClientId}?start_date=${startDate}&end_date=${endDate}`).then(r => r.data),
    enabled: selectedClientId !== 'all' && !!startDate && !!endDate,
  });

  if (loading) return <LoadingState />;
  if (!data) return null;

  const handleExport = () => {
    const headers = ['Customer', 'Type', 'Quotes', 'Accepted', 'Declined', 'Conversion %', 'Total Quoted', 'Total Invoiced', 'Paid', 'Outstanding'];
    const rows = data.items.map(r => [
      r.client_name, r.client_type, r.quote_count, r.accepted_count, r.declined_count,
      `${r.conversion_rate}%`, r.total_quoted, r.total_invoiced, r.total_paid, r.outstanding
    ]);
    const summary = ['', '', data.total_quotes, data.total_accepted, data.total_declined, `${data.overall_conversion_rate}%`, data.grand_quoted, data.grand_invoiced, '', ''];
    onExport('Customer Analytics Report', headers, rows, summary);
  };

  const toggleExpand = (clientId) => {
    setExpandedClient(expandedClient === clientId ? null : clientId);
  };

  return (
    <div className="space-y-5" data-testid="clients-tab">
      <div className="bg-white rounded-[10px] border p-4 flex flex-wrap items-center gap-4 print:hidden">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Select Customer</label>
          <select
            value={selectedClientId}
            onChange={e => { setSelectedClientId(e.target.value); setExpandedClient(null); }}
            className="px-3 py-2 border border-slate-300 rounded text-sm w-64"
            data-testid="client-selector"
          >
            <option value="all">All Customers</option>
            {data.items.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
            ))}
          </select>
        </div>
        {selectedClientId !== 'all' && (
          <button onClick={() => setSelectedClientId('all')} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 hover:text-slate-800 border rounded hover:bg-slate-50 mt-4" data-testid="back-to-all-btn">
            <ArrowLeft size={14} /> Back to All Customers
          </button>
        )}
      </div>

      {selectedClientId === 'all' ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-[10px] border p-4">
              <div className="text-xs font-medium text-slate-500 mb-1">Total Customers</div>
              <div className="text-2xl font-bold text-slate-800">{data.count}</div>
            </div>
            <div className="bg-white rounded-[10px] border p-4">
              <div className="text-xs font-medium text-slate-500 mb-1">Total Quotes</div>
              <div className="text-2xl font-bold text-slate-800">{data.total_quotes}</div>
              <div className="text-xs text-green-600 mt-0.5">{data.total_accepted} accepted</div>
            </div>
            <div className="bg-white rounded-[10px] border p-4">
              <div className="text-xs font-medium text-slate-500 mb-1">Conversion Rate</div>
              <div className="text-2xl font-bold text-[#f4931d]">{data.overall_conversion_rate}%</div>
              <div className="text-xs text-red-500 mt-0.5">{data.total_declined} declined</div>
            </div>
            <div className="bg-white rounded-[10px] border p-4">
              <div className="text-xs font-medium text-slate-500 mb-1">Total Quoted</div>
              <div className="text-2xl font-bold text-slate-800">{fmt(data.grand_quoted)}</div>
            </div>
            <div className="bg-white rounded-[10px] border p-4">
              <div className="text-xs font-medium text-slate-500 mb-1">Total Invoiced</div>
              <div className="text-2xl font-bold text-green-700">{fmt(data.grand_invoiced)}</div>
            </div>
          </div>

          <div className="flex justify-end print:hidden">
            <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700" data-testid="export-clients-excel">
              <FileSpreadsheet size={14} /> Export Excel
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <CustomerConversionChart data={data.conversion_chart} />
            <CustomerRevenueChart data={data.revenue_chart} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <DonutChart data={data.by_type} title="Customers by Type" />
            <DonutChart data={data.by_referral} title="Customers by Referral Source" />
          </div>

          <div className="bg-white rounded-[10px] border overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-slate-600 w-8"></th>
                  <th className="px-3 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Customer</th>
                  <th className="px-3 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Type</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Quotes</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Accepted</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Declined</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Conversion</th>
                  <th className="px-3 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Total Quoted</th>
                  <th className="px-3 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Invoiced</th>
                  <th className="px-3 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Paid</th>
                  <th className="px-3 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Outstanding</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Repeat</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.items.length === 0 ? (
                  <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-400">No customers with activity in this period</td></tr>
                ) : data.items.map((r) => (
                  <React.Fragment key={r.client_id}>
                    <tr className="hover:bg-orange-50/30 transition-colors cursor-pointer" onClick={() => toggleExpand(r.client_id)} data-testid={`client-row-${r.client_id}`}>
                      <td className="px-3 py-2.5 text-slate-400">
                        <ChevronDown size={14} className={`transition-transform ${expandedClient === r.client_id ? 'rotate-180' : ''}`} />
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedClientId(r.client_id); }} className="font-medium text-[#f4931d] hover:underline" data-testid={`view-client-${r.client_id}`}>
                          {r.client_name}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.client_type}</td>
                      <td className="px-3 py-2.5 text-center font-medium">{r.quote_count}</td>
                      <td className="px-3 py-2.5 text-center text-green-700 font-medium">{r.accepted_count}</td>
                      <td className="px-3 py-2.5 text-center text-red-600 font-medium">{r.declined_count}</td>
                      <td className="px-3 py-2.5 text-center"><ConversionBadge rate={r.conversion_rate} /></td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{fmt(r.total_quoted)}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{fmt(r.total_invoiced)}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap text-green-700">{fmt(r.total_paid)}</td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">{r.outstanding > 0 ? <span className="text-red-600">{fmt(r.outstanding)}</span> : fmt(0)}</td>
                      <td className="px-3 py-2.5 text-center">{r.is_repeat ? <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span> : <span className="inline-block w-2 h-2 rounded-full bg-slate-300"></span>}</td>
                    </tr>
                    {expandedClient === r.client_id && (
                      <tr><td colSpan={12} className="p-0"><ClientDrilldown client={r} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              {data.items.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-800 text-white font-semibold">
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3">TOTALS</td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3 text-center">{data.total_quotes}</td>
                    <td className="px-3 py-3 text-center">{data.total_accepted}</td>
                    <td className="px-3 py-3 text-center">{data.total_declined}</td>
                    <td className="px-3 py-3 text-center">{data.overall_conversion_rate}%</td>
                    <td className="px-3 py-3 text-right">{fmt(data.grand_quoted)}</td>
                    <td className="px-3 py-3 text-right">{fmt(data.grand_invoiced)}</td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      ) : (
        <ClientDetailView detail={clientDetail.data} loading={clientDetail.isLoading} onExport={onExport} />
      )}
    </div>
  );
}
